/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    LinearProgress,
    Stack,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { uploadPersistedData } from "apis/api";
import { uploadCustomAdjustments } from "apis/api";
import { executeCustomAdjustmentsCalcul } from "apis/api";


export const ExcelUpload = ({ onClose, onUploadComplete }) => {
    const [file, setFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [fileType, setFileType] = useState("");

    // const s3Client = new S3Client({
    //     region: process.env.REACT_APP_AWS_REGION,
    //     credentials: {
    //         accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    //         secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
    //     },
    // });

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        const allowedTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
        ];

        if (selectedFile && allowedTypes.includes(selectedFile.type)) {
            setFile(selectedFile);
            setUploadStatus("");
        } else {
            setFile(null);
            setUploadStatus("Please select a valid Excel or CSV file");
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setUploadStatus("Please select a file first");
            return;
        }

        try {
            if (fileType === "persistedData") {
                setIsUploading(true);
                setUploadStatus("");

                // Call your external API function
                const result = await uploadPersistedData(file);


                setUploadStatus("File uploaded successfully!");
                setFile(null);

                if (fileInputRef.current) fileInputRef.current.value = "";
            } else {
                setIsUploading(true);
                setUploadStatus("");

                // Call your external API function
                // NOTE: errors are deliberately not swallowed here — the calculation lambda
                // rewrites the whole custom-adjustments table, so a failed run must not be
                // reported to the user as a success.
                const result = await uploadCustomAdjustments(file);
                await executeCustomAdjustmentsCalcul();

                console.log("Server response:", result);

                setUploadStatus("File uploaded successfully!");
                setFile(null);

                if (fileInputRef.current) fileInputRef.current.value = "";

                // Notify the parent that the adjustments were re-calculated, so the report
                // re-reads them instead of using the copy it fetched on mount.
                if (onUploadComplete) onUploadComplete("customAdjustments");
            }

        } catch (error) {
            console.error("Upload error:", error);
            setUploadStatus("Upload failed. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: 3, p: 1 }
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: 600,
                    fontSize: "1.3rem",
                    textAlign: "center",
                    pb: 1
                }}
            >
                Upload Excel / CSV File
            </DialogTitle>

            <DialogContent dividers sx={{ borderRadius: 2 }}>
                <Stack spacing={3}>

                    {/* Larger Select Input */}
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel id="file-type-label" sx={{ fontSize: "1rem" }}>
                            Select File Type
                        </InputLabel>
                        <Select
                            labelId="file-type-label"
                            value={fileType}
                            label="Select File Type"
                            onChange={(e) => setFileType(e.target.value)}
                            sx={{
                                borderRadius: 2,
                                height: 55,           // 🔥 Bigger height
                                fontSize: "1rem",     // Larger text
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            <MenuItem value="customAdjustments" sx={{ py: 2 }}>
                                📄 Custom Adjustments File
                            </MenuItem>

                            <MenuItem value="persistedData" sx={{ py: 2 }}>
                                📘 Persisted Data File
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {/* File Picker Button with custom text color */}
                    <Button
                        variant="contained"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        sx={{
                            py: 1.3,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "1rem",
                            backgroundColor: "#1976d2",
                            color: "#ffffff !important",    // 🔥 Text color
                            "&:hover": { backgroundColor: "#1565c0" },
                        }}
                    >
                        Choose File
                        <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            hidden
                            onChange={handleFileChange}
                            ref={fileInputRef}
                        />
                    </Button>

                    {/* File Selected Info */}
                    {file && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                backgroundColor: "#f3f4f6",
                                border: "1px solid #e0e0e0",
                            }}
                        >
                            <Typography variant="body2" color="text.primary">
                                <strong>Selected File:</strong> {file.name}
                            </Typography>
                        </Box>
                    )}

                    {/* Upload Status */}
                    {uploadStatus && (
                        <Alert
                            severity={uploadStatus.includes("successfully") ? "success" : "error"}
                            sx={{ borderRadius: 2 }}
                        >
                            {uploadStatus}
                        </Alert>
                    )}

                    {isUploading && <LinearProgress sx={{ borderRadius: 2 }} />}

                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    sx={{
                        textTransform: "none",
                        color: "#f44336",
                        borderColor: "#f44336",
                        borderRadius: 2,
                        "&:hover": {
                            backgroundColor: "#fdecea",
                            borderColor: "#f44336",
                        },
                    }}
                >
                    Close
                </Button>

                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!file || !fileType || isUploading}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#1565c0" },
                        px: 4
                    }}
                >
                    {isUploading ? "Uploading..." : "Upload"}
                </Button>
            </DialogActions>
        </Dialog>



    );
};
